/**
 * Portal domain model — vertical, roles, capabilities, and the institution
 * a given portal instance is serving.
 *
 * Phase SC-4b: the institution is now **API-derived**. It used to be a
 * static fixture keyed by slug (three seeded tenants, everything else
 * falling back to a school named "Bright Future Public School"), which
 * meant a real newly-onboarded study centre would have been served school
 * nav and school vocabulary. `institutionFromProfile` builds it from
 * `GET /tenants/{slug}/public-profile` instead, so the vertical always
 * comes from the type the super admin actually picked.
 *
 * Two things still live on the client, deliberately:
 *   - **capabilities** per vertical (the anonymous profile read does not
 *     expose a tenant's module list; module-gated nav needs an
 *     authenticated portal-context read — deferred to SC-4c)
 *   - **hero copy** per vertical, which is platform-authored voice rather
 *     than a claim about a specific institution
 */
import type { Palette } from "@eduviaos/ui-kit";

/** The three EduViaOS verticals a single portal must serve. */
export type Vertical = "school" | "study_centre" | "coaching_institute";

/** Portal-facing user roles. (Distinct from the 9 *color* roles.) */
export type PortalRole = "student" | "guardian" | "team";

/** Personas served by the learner/guardian shell (Ask Ziggy, learner nav). */
export type LearnerPortalRole = Exclude<PortalRole, "team">;

/**
 * Age band drives what a learner can self-serve vs. what routes through a
 * guardian (age-gating agreed in the foundation design):
 *   - k5     → parent-proxy: guardian acts on the learner's behalf
 *   - middle → limited: learner sees content, guardian holds consent
 *   - senior → full: learner self-serves, guardian gets visibility
 */
export type AgeBand = "k5" | "middle" | "senior";

/**
 * Capability keys gate which shell modules render. An institution enables
 * a subset per vertical; the shell reads this list rather than hard-coding
 * per-vertical nav (capability-driven, agreed in the foundation design).
 */
export type PortalCapability =
  | "timetable"
  | "attendance"
  | "assignments"
  | "assessments"
  | "seating"
  | "fees"
  | "messages"
  | "achievements"
  | "resources";

export interface InstitutionTheme {
  /** One of the shared 9-role palette presets, or "custom". */
  palette: Palette;
  /** Hex primary when palette === "custom" (institution brand color). */
  customColor: string | null;
}

export interface Institution {
  slug: string;
  name: string;
  /** Display-only trim of `name` (see `displayShortName`). */
  shortName: string;
  /** Platform-authored line for the vertical, not a tenant claim. */
  tagline: string;
  vertical: Vertical;
  /** Phase SC-4 — SA/IA can disable the learner app module. Seat-only mode when false. */
  learnerAppOffered: boolean;
  theme: InstitutionTheme;
  capabilities: PortalCapability[];
}

const SCHOOL_CAPS: PortalCapability[] = [
  "timetable",
  "attendance",
  "assignments",
  "assessments",
  "fees",
  "messages",
  "achievements",
  "resources",
];

const STUDY_CENTRE_CAPS: PortalCapability[] = [
  "seating",
  "attendance",
  "assessments",
  "fees",
  "messages",
  "achievements",
  "resources",
];

const COACHING_CAPS: PortalCapability[] = [
  "timetable",
  "assignments",
  "assessments",
  "attendance",
  "fees",
  "messages",
  "achievements",
  "resources",
];

const CAPABILITIES_BY_VERTICAL: Record<Vertical, PortalCapability[]> = {
  school: SCHOOL_CAPS,
  study_centre: STUDY_CENTRE_CAPS,
  coaching_institute: COACHING_CAPS,
};

/**
 * Hero copy on the login screen. Per **vertical**, never per tenant: the
 * platform can promise a study centre's learner a seat and a pace, but it
 * cannot invent a specific centre's motto.
 */
const TAGLINE_BY_VERTICAL: Record<Vertical, string> = {
  school: "Everything school, all in one happy place.",
  study_centre: "Your seat. Your focus. Your pace.",
  coaching_institute: "Rank up. Every single day.",
};

/** Slug used in local dev, where the host carries no tenant. */
export const DEFAULT_TENANT_SLUG = "quiet-corner";

/**
 * Trim a trailing vertical suffix for the compact header slot.
 *
 * The backend has no `short_name` field, and the header already prints the
 * vertical underneath the name, so "Quiet Corner Study Centre / Study
 * Centre" reads as a stutter. This is display-only: nothing is invented,
 * we only drop a suffix the institution itself put there. If trimming
 * would leave nothing, the full name wins.
 */
export function displayShortName(name: string): string {
  const suffixes = [
    "Study Centre",
    "Study Center",
    "Coaching Institute",
    "Coaching Centre",
    "Coaching Center",
    "Public School",
    "High School",
    "School",
    "Institute",
    "Academy",
  ];
  const trimmed = name.trim();
  for (const suffix of suffixes) {
    if (trimmed.toLowerCase().endsWith(suffix.toLowerCase())) {
      const head = trimmed.slice(0, trimmed.length - suffix.length).trim();
      // "The School" → "The" is not a name anyone recognises; keep the
      // full name whenever the remainder is too short to stand alone.
      if (head.length >= 4) return head;
    }
  }
  return trimmed;
}

/** Build the shell's institution from the anonymous tenant profile read. */
export function institutionFromProfile(profile: {
  slug: string;
  name: string;
  vertical: Vertical;
  learner_app_offered?: boolean;
  branding?: { primary_color?: string | null } | null;
}): Institution {
  const customColor = profile.branding?.primary_color ?? null;
  const learnerAppOffered =
    profile.vertical === "study_centre" ? (profile.learner_app_offered ?? true) : false;
  return {
    slug: profile.slug,
    name: profile.name,
    shortName: displayShortName(profile.name),
    tagline: TAGLINE_BY_VERTICAL[profile.vertical],
    vertical: profile.vertical,
    learnerAppOffered,
    // A tenant that has set a brand colour drives the palette off it; one
    // that hasn't gets the platform default rather than a colour we made
    // up for them. `TenantBrandingProvider` writes the hex into the CSS
    // vars either way.
    theme: {
      palette: customColor ? "custom" : "default",
      customColor,
    },
    capabilities: CAPABILITIES_BY_VERTICAL[profile.vertical],
  };
}

const VERTICAL_LABELS: Record<Vertical, string> = {
  school: "School",
  study_centre: "Study Centre",
  coaching_institute: "Coaching Institute",
};

export function verticalLabel(v: Vertical): string {
  return VERTICAL_LABELS[v];
}
